import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import {
  errorMessagesActions,
  Messages,
  errorTypes,
  apiCallFinished,
  ScreenNav,
  CalendarWithFilters,
  scrollToMessageTypeAnchor,
  withRestorableDraft,
  withProgressBar,
  calendarSelectors,
  Title,
} from '~/components';

import { appointmentDraftActions } from '~/api';
import { triggerSelectTimeSlotAnalytics } from '~/analytics/analytics.listener';

import { loadFittedBedroomsSlots, setFilters } from './select-date.actions';

import { scrollToAnchor } from '~/utils';
import { dateUtils } from '../utils';
import { eligibleSlotsFilter as defaultEligibleSlotsFilter } from '../../components/calendar-with-filters/date-filters';

const ScrollToMessageTypeAnchor = scrollToMessageTypeAnchor(({ title }) => <Title>{title}</Title>);

const { SLOT_DATE_NOT_SELECTED_ERROR, SLOT_TIME_NOT_SELECTED_ERROR } = errorTypes;

const getEligibleSlotsFilter = (config) =>
  config.Calendar && config.Calendar.eligibleSlotsFilter
    ? config.Calendar.eligibleSlotsFilter
    : defaultEligibleSlotsFilter;

export class FittedBedroomsSelectDate extends PureComponent {
  state = {
    isAjaxCompleted: false,
  };

  componentDidMount() {
    this.loadSlots();

    window.scrollTo(0, 0);
  }

  componentDidUpdate({ selectedDateSlot: prevSelectedDateSlot }) {
    const {
      selectedDateSlot,
      selectedTimeSlot,
      slotErrorMessages,
      dispatchRemoveSingleErrorMessage,
    } = this.props;

    if (slotErrorMessages[SLOT_DATE_NOT_SELECTED_ERROR] && selectedDateSlot.times.length) {
      dispatchRemoveSingleErrorMessage(SLOT_DATE_NOT_SELECTED_ERROR);
    }

    if (slotErrorMessages[SLOT_TIME_NOT_SELECTED_ERROR] && selectedTimeSlot.from) {
      dispatchRemoveSingleErrorMessage(SLOT_TIME_NOT_SELECTED_ERROR);
    }

    if (prevSelectedDateSlot.date === '' && selectedDateSlot.date) {
      scrollToAnchor('time-slot-title');
    }
  }

  saveSlot = () => {
    const {
      history,
      dispatchSaveSlot,
      selectedDateSlot,
      selectedTimeSlot,
      dispatchResetErrorMessagesWith,
      dispatchApiCallFinished,
    } = this.props;
    if (!selectedDateSlot.times.length) {
      dispatchResetErrorMessagesWith([{ type: SLOT_DATE_NOT_SELECTED_ERROR }]);
    } else if (!selectedTimeSlot.from) {
      dispatchResetErrorMessagesWith([{ type: SLOT_TIME_NOT_SELECTED_ERROR }]);
    } else {
      dispatchApiCallFinished(false);
      dispatchSaveSlot().then(() => {
        history.pushWithToken('/about-you');
      });
    }
  };

  loadSlots = () => {
    const {
      dispatchLoadFittedBedroomsSlots,
      dispatchApiCallFinished,
      selectedDateSlot: { date },
      postcode,
      config,
    } = this.props;

    dispatchApiCallFinished(false);

    const startDate = dateUtils.getSlotsStartDate(date);

    dispatchLoadFittedBedroomsSlots(postcode, startDate, getEligibleSlotsFilter(config)).then(
      () => {
        dispatchApiCallFinished(true);
        this.setState({
          isAjaxCompleted: true,
        });
      },
    );
  };

  selectDateSlot = (selectedDateSlot) => {
    const { dispatchSelectDateSlot, dispatchRemoveSingleErrorMessage } = this.props;
    if (selectedDateSlot) {
      dispatchSelectDateSlot(selectedDateSlot.date);
      dispatchRemoveSingleErrorMessage(SLOT_DATE_NOT_SELECTED_ERROR);
    } else {
      dispatchRemoveSingleErrorMessage(SLOT_TIME_NOT_SELECTED_ERROR);
      dispatchSelectDateSlot(null);
    }
  };

  selectTimeSlot = (selectedTimeSlot) => {
    const { dispatchSelectTimeSlot, dispatchRemoveSingleErrorMessage } = this.props;
    if (selectedTimeSlot) {
      dispatchRemoveSingleErrorMessage(SLOT_TIME_NOT_SELECTED_ERROR);
    }
    dispatchSelectTimeSlot(selectedTimeSlot);
    triggerSelectTimeSlotAnalytics();
  };

  render() {
    const {
      filters,
      slots,
      maxAdvance,
      selectedDateSlot,
      selectedTimeSlot,
      selectedShopName,
      config: { Calendar = {} },
      dispatchApplyFilters,
      dispatchApiCallFinished,
    } = this.props;
    const {
      calendarTimeSlotsIntro: CalendarTimeSlotsIntro,
      timeSlotsList,
      timeSlotSectionTitle,
      eligibleSlotsFilter = defaultEligibleSlotsFilter,
      formatTimeSlots,
    } = Calendar;

    const { isAjaxCompleted } = this.state;

    return (
      <section data-testid="SelectDate" data-test-ajax={isAjaxCompleted}>
        <ScrollToMessageTypeAnchor
          title="Choose a date for your appointment"
          forTypes={[SLOT_DATE_NOT_SELECTED_ERROR]}
        />
        <CalendarWithFilters
          filters={filters}
          slots={slots}
          selectedDateSlot={selectedDateSlot}
          selectedTimeSlot={selectedTimeSlot}
          timeSlotsList={timeSlotsList}
          timeSlotSectionTitle={timeSlotSectionTitle}
          applyFiltersHandler={dispatchApplyFilters}
          selectDateSlot={this.selectDateSlot}
          selectTimeSlot={this.selectTimeSlot}
          extraClassName="SelectDateBordered"
          apiCallFinished={dispatchApiCallFinished}
          maxAdvance={maxAdvance}
          eligibleSlotsFilter={eligibleSlotsFilter}
          formatTimeSlots={formatTimeSlots}
        >
          <Messages forTypes={[SLOT_DATE_NOT_SELECTED_ERROR]} />
          <Messages forTypes={[SLOT_TIME_NOT_SELECTED_ERROR]} />
          {CalendarTimeSlotsIntro && <CalendarTimeSlotsIntro shopName={selectedShopName} />}
        </CalendarWithFilters>
        <ScreenNav onNextClick={this.saveSlot} prevPath="/shops" />
      </section>
    );
  }
}

export const mapStateToProps = ({
  selectDate: { slots, filters },
  services: { config },
  appointment: {
    draft: {
      dateSlot,
      timeSlot,
      shop: { name },
      appointmentType: { maxAdvance },
    },
  },
  journeyState: { searchQuery: postcode },
  messages: {
    app: { errorMessages: slotErrorMessages },
  },
}) => {
  const { selectSlotsByFilters, selectDateSlot, selectTimeSlot, selectSlotMessages } =
    calendarSelectors;
  const selectedSlots = selectSlotsByFilters(slots, filters, getEligibleSlotsFilter(config));
  const selectedDateSlot = selectDateSlot(selectedSlots, dateSlot);
  const selectedTimeSlot = selectTimeSlot(selectedDateSlot, timeSlot);
  const selectedSlotErrorMessages = selectSlotMessages(slotErrorMessages);

  return {
    slots: selectedSlots,
    filters,
    maxAdvance,
    selectedDateSlot,
    selectedTimeSlot,
    selectedShopName: name,
    config,
    prevPath: config.selectDatePrevPath,
    postcode,
    slotErrorMessages: selectedSlotErrorMessages,
  };
};

const mapDispatchToProps = {
  dispatchLoadFittedBedroomsSlots: loadFittedBedroomsSlots,

  dispatchSaveSlot: appointmentDraftActions.saveAppointmentDraft,
  dispatchSelectDateSlot: appointmentDraftActions.setDateSlot,
  dispatchSelectTimeSlot: appointmentDraftActions.setTimeSlot,

  dispatchResetErrorMessagesWith: errorMessagesActions.resetErrorMessagesWith,
  dispatchRemoveSingleErrorMessage: errorMessagesActions.removeSingleErrorMessage,
  dispatchApiCallFinished: apiCallFinished,
  dispatchApplyFilters: setFilters,
};

export const ConnectedFittedBedroomsSelectDate = withRestorableDraft(
  connect(mapStateToProps, mapDispatchToProps)(withProgressBar(FittedBedroomsSelectDate)),
);

const timeSlotModel = {
  eventId: PropTypes.string.isRequired,
  from: PropTypes.number.isRequired,
  to: PropTypes.number,
};

const dateSlotModel = {
  date: PropTypes.string.isRequired,
  times: PropTypes.arrayOf(PropTypes.shape(timeSlotModel)),
};

FittedBedroomsSelectDate.propTypes = {
  history: PropTypes.shape({}).isRequired,
  slots: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  filters: PropTypes.arrayOf(PropTypes.string),
  maxAdvance: PropTypes.number.isRequired,
  postcode: PropTypes.string.isRequired,

  selectedDateSlot: PropTypes.shape(dateSlotModel),
  selectedTimeSlot: PropTypes.shape(timeSlotModel),
  selectedShopName: PropTypes.string.isRequired,
  slotErrorMessages: PropTypes.shape({
    type: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
  }).isRequired,
  config: PropTypes.shape({
    chosenPartner: PropTypes.func,
    partnerTitle: PropTypes.string,
    Calendar: PropTypes.shape({
      calendarTimeSlotsIntro: PropTypes.func,
      timeSlotsList: PropTypes.func,
      timeSlotSectionTitle: PropTypes.string,
      formatTimeSlots: PropTypes.func,
    }),
  }).isRequired,

  dispatchSelectDateSlot: PropTypes.func.isRequired,
  dispatchSelectTimeSlot: PropTypes.func.isRequired,
  dispatchResetErrorMessagesWith: PropTypes.func.isRequired,
  dispatchRemoveSingleErrorMessage: PropTypes.func.isRequired,
  dispatchSaveSlot: PropTypes.func.isRequired,
  dispatchLoadFittedBedroomsSlots: PropTypes.func.isRequired,
  dispatchApiCallFinished: PropTypes.func.isRequired,
  dispatchApplyFilters: PropTypes.func.isRequired,
};

FittedBedroomsSelectDate.defaultProps = {
  filters: [],
  selectedDateSlot: {
    date: '',
    times: [],
  },
  selectedTimeSlot: {
    eventId: '',
    from: 0,
  },
};
